/**
 * The page table. Each screen WP appends ONE line to `routes` (its path and how it renders); nothing else here changes.
 * The shell matches the pathname, resolves the team from `authMe` and draws the rail and top bar around the page
 * (`bare` pages draw their own full-bleed stage).
 */
import type { ReactNode } from "react";
import type { Principal } from "@bsim/contracts";
import type { ApiClient } from "./api/client";
import { JoinPage, JOIN_PATH, SIGN_IN_PATH, SignInPage, VERIFY_PATH, VerifyPage } from "./auth";
import { BankPage } from "./bank/BankPage";
import { ChairPage } from "./chair/ChairPage";
import { CheckInPage } from "./checkin/CheckInPage";
import { CheckPage } from "./check";
import { CockpitPage } from "./cockpit/CockpitPage";
import { DecidePage } from "./decisions/DecidePage";
import { FacilitatorFrame, BoardPage, RosterPage, StudioPage } from "./facilitator";
import { FinancialsPage } from "./financials/FinancialsPage";
import { OutlookPage } from "./outlook/OutlookPage";
import { ResultsLatest, ResultsPage, PackPage } from "./results";
import { ComparePage, RunsPage } from "./runs";
import { RoomScreenPage } from "./room";
import { SoloFinalPage, SoloRunPage, SoloStartPage } from "./solo";
import { GxErrorBanner } from "./theme/primitives";

export interface PageRoute {
  readonly path: string;
  readonly render: (params: Readonly<Record<string, string>>, deps: PageDeps) => ReactNode;
  readonly bare?: boolean;
}

export interface PageDeps {
  readonly api: ApiClient;
  readonly principal: Principal;
  /** The viewer's team in the route's cohort; null for facilitators, observers and people not yet placed. */
  readonly teamId: string | null;
}

/** Pages that need no sign-in: the shell renders them before it asks who the viewer is. */
export const openPages: Readonly<Record<string, (api: ApiClient) => ReactNode>> = {
  "/check": () => <CheckPage />,
  [SIGN_IN_PATH]: (api) => <SignInPage requestLink={api.route("authRequestMagicLink")} />,
  [VERIFY_PATH]: (api) => <VerifyPage verify={api.route("authVerifyMagicLink")} />,
  [JOIN_PATH]: (api) => <JoinPage join={api.route("authJoinCode")} />,
};

export const routes: PageRoute[] = [
  { path: "/c/:cohortId/bank", bare: true, render: (p, d) => <BankPage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} /> },
  { path: "/c/:cohortId/cockpit", render: (p, d) => <CockpitPage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} /> },
  { path: "/c/:cohortId/financials", render: (p, d) => <FinancialsPage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} /> },
  { path: "/c/:cohortId/outlook", render: (p, d) => <OutlookPage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} /> },
  { path: "/c/:cohortId/results", render: (p) => <ResultsLatest cohortId={p["cohortId"] ?? ""} /> },
  { path: "/results", render: () => <ResultsLatest cohortId={null} /> },
  { path: "/c/:cohortId/results/:roundNo", render: (p, d) => <ResultsPage cohortId={p["cohortId"] ?? ""} roundNo={Number(p["roundNo"])} api={d.api} /> },
  { path: "/c/:cohortId/results/:roundNo/pack", bare: true, render: (p, d) => <PackPage cohortId={p["cohortId"] ?? ""} roundNo={Number(p["roundNo"])} principal={d.principal} /> },
  { path: "/c/:cohortId/decide", render: (p, d) => d.teamId === null ? <GxErrorBanner message="Your facilitator hasn't placed you on a team yet." /> : <DecidePage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} api={d.api} /> },
  { path: "/c/:cohortId/chair", render: (p, d) => d.teamId === null ? <GxErrorBanner message="Your facilitator hasn't placed you on a team yet." /> : <ChairPage cohortId={p["cohortId"] ?? ""} teamId={d.teamId} api={d.api} /> },
  { path: "/f/:cohortId/board", bare: true, render: (p, d) => <FacilitatorFrame cohortId={p["cohortId"] ?? ""} page="board" displayName={d.principal.displayName}><BoardPage cohortId={p["cohortId"] ?? ""} api={d.api} /></FacilitatorFrame> },
  { path: "/f/:cohortId/roster", bare: true, render: (p, d) => <FacilitatorFrame cohortId={p["cohortId"] ?? ""} page="roster" displayName={d.principal.displayName}><RosterPage cohortId={p["cohortId"] ?? ""} api={d.api} /></FacilitatorFrame> },
  { path: "/f/:cohortId/studio", bare: true, render: (p, d) => <FacilitatorFrame cohortId={p["cohortId"] ?? ""} page="studio" displayName={d.principal.displayName}><StudioPage cohortId={p["cohortId"] ?? ""} api={d.api} /></FacilitatorFrame> },
  { path: "/f/:cohortId/studio/assessment", bare: true, render: (p, d) => <FacilitatorFrame cohortId={p["cohortId"] ?? ""} page="studio" displayName={d.principal.displayName}><StudioPage cohortId={p["cohortId"] ?? ""} api={d.api} tab="assessment" /></FacilitatorFrame> },
  { path: "/room/:cohortId", bare: true, render: (p, d) => <RoomScreenPage cohortId={p["cohortId"] ?? ""} api={d.api} /> },
  { path: "/c/:cohortId/check-in/pre", bare: true, render: (p, d) => <CheckInPage cohortId={p["cohortId"] ?? ""} phase="pre" api={d.api} /> },
  { path: "/c/:cohortId/check-in/post", bare: true, render: (p, d) => <CheckInPage cohortId={p["cohortId"] ?? ""} phase="post" api={d.api} /> },
  { path: "/runs", bare: true, render: (_p, d) => <RunsPage memberships={d.principal.memberships} userId={d.principal.userId} /> },
  { path: "/runs/compare", bare: true, render: () => <ComparePage /> },
  { path: "/solo", bare: true, render: (_p, d) => <SoloStartPage api={d.api} /> },
  { path: "/solo/:runId", render: (p, d) => <SoloRunPage runId={p["runId"] ?? ""} api={d.api} /> },
  { path: "/solo/:runId/final", bare: true, render: (p) => <SoloFinalPage runId={p["runId"] ?? ""} /> },
];
